import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Calendar, Tag, Clock, User, Share2,
  Twitter, Linkedin, Facebook, Link as LinkIcon, Sparkles,
  Waves, Sun, TreePine, Cloud, X, ChevronLeft, ChevronRight, Globe2, Check,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicArticles, fetchPublicArticle } from '../services/newsService';
import { absoluteMediaUrl } from '../admin/api';


/* =========================================================
 * THEME SYSTEM
 * Each article has a `theme` (sea / sand / sky / trees).
 * Theme values control: hero overlay, accent gradients,
 * chip colors, divider style and ambient background glow.
 * ========================================================= */
const THEMES = {
  sea: {
    name_ar: 'البحر', name_en: 'Sea',
    Icon: Waves,
    chipClass:
      'bg-cyan-50 text-cyan-900 border-cyan-300/70 dark:bg-cyan-500/20 dark:text-cyan-100 dark:border-cyan-300/40',
    accentText: 'text-cyan-700 dark:text-cyan-300',
    accentRing: 'border-cyan-500/40',
    accentBg:
      'bg-gradient-to-br from-cyan-500 via-teal-500 to-brand-blue-700',
    // Light: clean cyan-tinted glass. Dark: deep ocean (cobalt → navy)
    accentBgSoft:
      'bg-gradient-to-b from-cyan-50/90 via-white to-sky-50/70 dark:from-[#062236] dark:via-[#082c45] dark:to-[#04162a]',
    // Color that matches the top of accentBgSoft — used to fill the wave divider
    // so it visually merges with the body
    dividerFill: 'text-[#ecfeff] dark:text-[#062236]',
    // Underwater feel — surface light fades to deep teal/navy
    heroOverlay:
      'linear-gradient(to bottom, rgba(8,90,120,0.30) 0%, rgba(6,78,108,0.55) 45%, rgba(2,44,72,0.92) 100%)',
    heroVignette:
      'radial-gradient(ellipse at center, rgba(34,211,238,0.25) 0%, rgba(0,0,0,0) 60%)',
    ambient1: 'bg-cyan-400/15 dark:bg-cyan-400/18',
    ambient2: 'bg-blue-400/12 dark:bg-blue-500/18',
    divider: 'wave',
    quoteBorder: 'border-cyan-500',
    quoteText: 'text-cyan-900 dark:text-cyan-100',
    quoteBg:
      'bg-gradient-to-r from-cyan-50/90 to-transparent dark:from-cyan-500/15 dark:via-teal-500/10 dark:to-transparent',
    pattern: 'sea',
  },
  sand: {
    name_ar: 'الرمال', name_en: 'Sand',
    Icon: Sun,
    chipClass:
      'bg-amber-50 text-amber-900 border-amber-300/70 dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-300/40',
    accentText: 'text-amber-700 dark:text-amber-300',
    accentRing: 'border-amber-500/40',
    accentBg:
      'bg-gradient-to-br from-amber-500 via-orange-500 to-brand-orange',
    // Light: warm beige glow. Dark: warm desert brown / espresso
    accentBgSoft:
      'bg-gradient-to-b from-amber-50/90 via-stone-50 to-orange-50/70 dark:from-[#2a1605] dark:via-[#1f1408] dark:to-[#1a0e05]',
    dividerFill: 'text-[#fffbeb] dark:text-[#2a1605]',
    // Sunset over dunes — golden top fading to warm dark brown
    heroOverlay:
      'linear-gradient(to bottom, rgba(180,90,20,0.18) 0%, rgba(120,53,15,0.55) 45%, rgba(38,17,5,0.94) 100%)',
    heroVignette:
      'radial-gradient(ellipse at center, rgba(245,158,11,0.28) 0%, rgba(0,0,0,0) 60%)',
    ambient1: 'bg-amber-400/18 dark:bg-amber-500/22',
    ambient2: 'bg-orange-400/15 dark:bg-orange-600/22',
    divider: 'dunes',
    quoteBorder: 'border-amber-500',
    quoteText: 'text-amber-900 dark:text-amber-100',
    quoteBg:
      'bg-gradient-to-r from-amber-50/90 to-transparent dark:from-amber-500/15 dark:via-orange-500/10 dark:to-transparent',
    pattern: 'sand',
  },
  sky: {
    name_ar: 'السماء', name_en: 'Sky',
    Icon: Cloud,
    chipClass:
      'bg-sky-50 text-sky-900 border-sky-300/70 dark:bg-sky-500/20 dark:text-sky-100 dark:border-sky-300/40',
    accentText: 'text-sky-700 dark:text-sky-200',
    accentRing: 'border-sky-400/40',
    accentBg:
      'bg-gradient-to-br from-sky-300 via-sky-500 to-brand-blue-600',
    // Light: airy white-blue glass. Dark: night sky — deep indigo/sky
    accentBgSoft:
      'bg-gradient-to-b from-sky-50/95 via-white to-blue-50/70 dark:from-[#0a1929] dark:via-[#0c2238] dark:to-[#0a1828]',
    dividerFill: 'text-[#f0f9ff] dark:text-[#0a1929]',
    // Bright sky at top, fading to night sky deep blue (no slate)
    heroOverlay:
      'linear-gradient(to bottom, rgba(186,225,255,0.06) 0%, rgba(14,52,96,0.40) 50%, rgba(7,28,58,0.92) 100%)',
    heroVignette:
      'radial-gradient(ellipse at center top, rgba(255,255,255,0.45) 0%, rgba(0,0,0,0) 60%)',
    ambient1: 'bg-sky-300/22 dark:bg-sky-400/22',
    ambient2: 'bg-white/50 dark:bg-indigo-400/18',
    divider: 'clouds',
    quoteBorder: 'border-sky-400',
    quoteText: 'text-sky-900 dark:text-sky-100',
    quoteBg:
      'bg-gradient-to-r from-sky-50/95 to-transparent dark:from-sky-500/15 dark:via-indigo-500/10 dark:to-transparent',
    pattern: 'sky',
  },
  trees: {
    name_ar: 'الأشجار', name_en: 'Trees',
    Icon: TreePine,
    chipClass:
      'bg-emerald-50 text-emerald-900 border-emerald-300/70 dark:bg-emerald-500/20 dark:text-emerald-100 dark:border-emerald-300/40',
    accentText: 'text-emerald-700 dark:text-emerald-300',
    accentRing: 'border-emerald-500/40',
    accentBg:
      'bg-gradient-to-br from-emerald-500 via-brand-green to-emerald-800',
    // Light: forest mist glass. Dark: deep forest green canopy
    accentBgSoft:
      'bg-gradient-to-b from-emerald-50/90 via-white to-green-50/70 dark:from-[#0a2418] dark:via-[#0a2b1c] dark:to-[#08200f]',
    dividerFill: 'text-[#ecfdf5] dark:text-[#0a2418]',
    // Forest canopy — moss green softness fading to deep forest dark
    heroOverlay:
      'linear-gradient(to bottom, rgba(6,80,46,0.25) 0%, rgba(6,55,30,0.55) 45%, rgba(3,30,15,0.93) 100%)',
    heroVignette:
      'radial-gradient(ellipse at center, rgba(16,185,129,0.25) 0%, rgba(0,0,0,0) 60%)',
    ambient1: 'bg-emerald-400/18 dark:bg-emerald-500/25',
    ambient2: 'bg-green-500/15 dark:bg-emerald-600/22',
    divider: 'leaves',
    quoteBorder: 'border-emerald-500',
    quoteText: 'text-emerald-900 dark:text-emerald-100',
    quoteBg:
      'bg-gradient-to-r from-emerald-50/90 to-transparent dark:from-emerald-500/15 dark:via-green-500/10 dark:to-transparent',
    pattern: 'trees',
  },
  // ============================================================
  // UNIFIED NATURE — blends the four ecosystems into one balanced
  // corporate theme. Best for cross-topic / general EBC stories.
  // ============================================================
  unified: {
    name_ar: 'بيئات التوازن', name_en: 'Unified Nature',
    Icon: Globe2,
    chipClass:
      'bg-gradient-to-r from-cyan-50 via-amber-50 to-emerald-50 text-slate-800 border-slate-300/70 dark:from-cyan-500/20 dark:via-amber-500/15 dark:to-emerald-500/20 dark:text-white dark:border-white/30',
    accentText: 'text-teal-700 dark:text-cyan-200',
    accentRing: 'border-teal-500/30',
    accentBg:
      'bg-gradient-to-br from-cyan-500 via-amber-400 to-emerald-500',
    // Light: subtle multi-tint glass. Dark: midnight canopy under starry sea
    accentBgSoft:
      'bg-gradient-to-b from-cyan-50/70 via-amber-50/40 to-emerald-50/70 dark:from-[#0a2030] dark:via-[#1a1a14] dark:to-[#0a2418]',
    dividerFill: 'text-[#ecfeff] dark:text-[#0a2030]',
    // Multi-environment dusk: warm horizon meeting deep ocean below
    heroOverlay:
      'linear-gradient(to bottom, rgba(20,80,90,0.22) 0%, rgba(60,35,20,0.42) 35%, rgba(8,40,30,0.78) 70%, rgba(4,20,18,0.94) 100%)',
    heroVignette:
      'radial-gradient(ellipse at center, rgba(245,158,11,0.16) 0%, rgba(34,211,238,0.14) 35%, rgba(0,0,0,0) 70%)',
    ambient1: 'bg-cyan-400/14 dark:bg-cyan-400/22',
    ambient2: 'bg-amber-400/14 dark:bg-amber-500/22',
    divider: 'unified',
    quoteBorder: 'border-teal-500',
    quoteText: 'text-slate-800 dark:text-slate-100',
    quoteBg:
      'bg-gradient-to-r from-cyan-50/70 via-amber-50/50 to-emerald-50/70 dark:from-cyan-500/12 dark:via-amber-500/10 dark:to-emerald-500/12',
    pattern: 'unified',
  },
};

/* Theme-specific shaped divider between hero and body */
const ThemedDivider = ({ kind, isAr }) => {
  // SVG color uses currentColor — caller sets text color via wrapper
  if (kind === 'wave') {
    return (
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-16 md:h-20">
        <path d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'dunes') {
    return (
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-16 md:h-20">
        <path d="M0,55 C200,20 400,70 720,40 C1040,10 1240,70 1440,30 L1440,80 L0,80 Z" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'clouds') {
    return (
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-16 md:h-20">
        <path d="M0,60 Q120,30 240,55 T480,50 Q600,30 720,55 T960,50 Q1080,30 1200,55 T1440,50 L1440,80 L0,80 Z" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'unified') {
    // A balanced multi-curve divider that hints at sea waves, dunes,
    // clouds and tree canopy in one continuous flowing line.
    return (
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-16 md:h-20">
        <defs>
          <linearGradient id="unified-divider-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="currentColor" stopOpacity="0.55" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
          </linearGradient>
        </defs>
        <path
          d="M0,50 C120,20 240,70 360,45 C480,20 600,60 720,40 C840,15 960,65 1080,50 C1200,30 1320,60 1440,42 L1440,80 L0,80 Z"
          fill="url(#unified-divider-grad)"
        />
        <path
          d="M0,60 C160,40 320,72 480,55 C640,38 800,68 960,52 C1120,38 1280,66 1440,55 L1440,80 L0,80 Z"
          fill="currentColor"
          opacity="0.55"
        />
      </svg>
    );
  }
  // leaves – soft organic curve
  return (
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="block w-full h-16 md:h-20">
      <path d="M0,45 C160,15 360,75 560,45 C760,15 960,75 1160,45 C1280,28 1360,55 1440,40 L1440,80 L0,80 Z" fill="currentColor" />
    </svg>
  );
};

const NewsArticlePage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      fetchPublicArticle(id, language),
      fetchPublicArticles(language),
    ])
      .then(([one, all]) => {
        if (!active) return;
        setArticle(one);
        setAllArticles(all);
      })
      .catch((e) => {
        if (!active) return;
        if (e?.response?.status === 404) setNotFound(true);
        setArticle(null);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, language]);

  const theme = THEMES[article?.theme] || THEMES.sea;

  const related = useMemo(
    () => (article ? allArticles.filter((a) => a.id !== article.id).slice(0, 3) : []),
    [article, allArticles]
  );

  // ===== Lightbox state =====
  const gallery = article?.gallery || [];
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed
  const isLightboxOpen = lightboxIndex !== null;

  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    () => setLightboxIndex((i) => (i === null ? null : (i - 1 + gallery.length) % gallery.length)),
    [gallery.length]
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? null : (i + 1) % gallery.length)),
    [gallery.length]
  );

  // Lock body scroll + keyboard nav while open
  useEffect(() => {
    if (!isLightboxOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') (isAr ? showNext : showPrev)();
      else if (e.key === 'ArrowRight') (isAr ? showPrev : showNext)();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isLightboxOpen, closeLightbox, showPrev, showNext, isAr]);

  // Derived values — safe defaults when article is still loading
  const title = isAr ? article?.title_ar : article?.title_en;
  const excerpt = isAr ? article?.excerpt_ar : article?.excerpt_en;
  const date = isAr ? article?.date_ar : article?.date_en;
  const tag = isAr ? article?.tag_ar : article?.tag_en;
  const author = isAr ? article?.author_ar : article?.author_en;
  const content = (isAr ? article?.content_ar : article?.content_en) || [];
  const pullQuote = isAr ? article?.pullQuote_ar : article?.pullQuote_en;
  const themeName = isAr ? theme.name_ar : theme.name_en;

  const handleCopyLink = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).then(showCopyToast, fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  // ===== Copy-link toast =====
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef(null);

  const showCopyToast = useCallback(() => {
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2400);
  }, []);

  // Fallback for browsers without clipboard API (e.g. old http contexts)
  const fallbackCopy = () => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = window.location.href;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showCopyToast();
    } catch {
      /* swallow */
    }
  };

  // Clear toast timer on unmount
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const ArrowLead = isAr ? ArrowRight : ArrowLeft;
  const ArrowTrail = isAr ? ArrowLeft : ArrowRight;

  // Early returns after ALL hooks are declared (React rules-of-hooks)
  if (loading) {
    return (
      <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-[80vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-800 border-t-brand-blue rounded-full animate-spin" />
      </div>
    );
  }
  if (!article || notFound) {
    return (
      <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-[70vh] flex flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'المقال غير موجود' : 'Article not found'}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 max-w-md">
          {isAr
            ? 'قد يكون المقال قد أُخفي، حُذف، أو أنّ الرابط غير صحيح.'
            : 'The article may have been unpublished, removed, or the link may be incorrect.'}
        </p>
        <Link
          to="/news"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:shadow-lg transition"
        >
          {isAr ? 'العودة إلى الأخبار' : 'Back to News'}
        </Link>
      </div>
    );
  }

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="relative">
      {/* Ambient theme glows in the background of the whole page */}
      <div className={`fixed top-[8%] -left-20 w-[500px] h-[500px] rounded-full blur-[120px] ${theme.ambient1} -z-10 pointer-events-none`} />
      <div className={`fixed top-[45%] -right-20 w-[500px] h-[500px] rounded-full blur-[120px] ${theme.ambient2} -z-10 pointer-events-none`} />
      {/* Extra ambient layers exclusively for the Unified Nature theme */}
      {article.theme === 'unified' && (
        <>
          <div className="fixed top-[25%] left-[40%] w-[500px] h-[500px] rounded-full blur-[120px] bg-emerald-400/12 -z-10 pointer-events-none" />
          <div className="fixed bottom-[10%] left-[10%] w-[400px] h-[400px] rounded-full blur-[120px] bg-sky-300/12 -z-10 pointer-events-none" />
        </>
      )}

      {/* ============ HERO ============ */}
      <section className="relative h-[80vh] min-h-[560px] overflow-hidden">
        <img
          src={absoluteMediaUrl(article.image)}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Themed overlays */}
        <div className="absolute inset-0" style={{ background: theme.heroOverlay }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: theme.heroVignette }} />

        {/* Back button */}
        <div className={`absolute top-24 ${isAr ? 'right-4 md:right-10' : 'left-4 md:left-10'} z-10`}>
          <Link
            to="/news"
            data-testid="article-back-button"
            className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full
                       bg-white/15 backdrop-blur-xl border border-white/25 text-white text-sm font-semibold
                       hover:bg-white/25 transition-all ${isAr ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLead className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            <span>{isAr ? 'العودة إلى الأخبار' : 'Back to News'}</span>
          </Link>
        </div>

        {/* Hero copy */}
        <div className="relative h-full flex items-end pb-16 md:pb-24 container mx-auto px-4">
          <div className={`max-w-4xl animate-fade-in-up ${isAr ? 'text-right' : 'text-left'}`}>
            {/* Theme + category chip */}
            <div className={`inline-flex items-center gap-2 mb-5 ${isAr ? 'flex-row-reverse' : ''}`}>
              <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-md ${theme.chipClass}`}>
                <theme.Icon className="w-3.5 h-3.5" />
                {tag}
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.25em] font-semibold bg-white/15 backdrop-blur-md border border-white/25 text-white">
                <Sparkles className="w-3 h-3" />
                {isAr ? `موضوع: ${themeName}` : `Theme: ${themeName}`}
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.1] mb-6"
              style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}
            >
              {title}
            </h1>

            {excerpt && (
              <p
                className="text-lg md:text-xl text-white/90 leading-loose max-w-3xl"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              >
                {excerpt}
              </p>
            )}

            {/* Meta row */}
            <div className={`mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/80 ${isAr ? 'flex-row-reverse justify-end' : ''}`}>
              <span className={`inline-flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                <Calendar className="w-4 h-4" />
                {date}
              </span>
              {article.readMinutes && (
                <span className={`inline-flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                  <Clock className="w-4 h-4" />
                  {isAr ? `قراءة ${article.readMinutes} دقائق` : `${article.readMinutes} min read`}
                </span>
              )}
              {author && (
                <span className={`inline-flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                  <User className="w-4 h-4" />
                  {author}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Themed divider transitioning into the body */}
        <div className={`absolute -bottom-px left-0 right-0 ${theme.dividerFill} pointer-events-none`}>
          <ThemedDivider kind={theme.divider} />
        </div>
      </section>

      {/* ============ BODY ============ */}
      <article className={`relative ${theme.accentBgSoft} pb-20`}>
        <div className="relative container mx-auto px-4 pt-4 md:pt-6">
          <div className="max-w-3xl mx-auto">
            {/* Share / actions toolbar */}
            <div
              className={`flex items-center justify-between gap-4 mb-10 pb-6 border-b border-slate-200 dark:border-slate-800
                          ${isAr ? 'flex-row-reverse' : ''}`}
              data-testid="article-toolbar"
            >
              <div className={`flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 ${isAr ? 'flex-row-reverse' : ''}`}>
                <Share2 className="w-4 h-4" />
                <span>{isAr ? 'شارك المقال' : 'Share article'}</span>
              </div>
              <div className={`flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                <ShareBtn
                  testId="share-twitter"
                  label="Twitter / X"
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}`}
                  Icon={Twitter}
                />
                <ShareBtn
                  testId="share-linkedin"
                  label="LinkedIn"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  Icon={Linkedin}
                />
                <ShareBtn
                  testId="share-facebook"
                  label="Facebook"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  Icon={Facebook}
                />
                <button
                  type="button"
                  data-testid="share-copy-link"
                  aria-label={isAr ? 'نسخ الرابط' : 'Copy link'}
                  onClick={handleCopyLink}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400 transition-all hover:-translate-y-0.5"
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article body */}
            <div
              className={`prose-styling space-y-6 text-base md:text-lg leading-[2] text-slate-800 dark:text-slate-200
                          ${isAr ? 'text-right' : 'text-left'}`}
              style={isAr ? { textAlign: 'justify', textAlignLast: 'right' } : undefined}
              data-testid="article-body"
            >
              {content.map((para, i) => (
                <React.Fragment key={i}>
                  {i === 0 ? (
                    /* drop cap on first paragraph */
                    <p
                      className="first-letter:text-5xl first-letter:font-bold first-letter:float-none first-letter:me-2 first-letter:leading-none"
                      style={isAr ? { textAlign: 'justify', textAlignLast: 'right' } : undefined}
                    >
                      {para}
                    </p>
                  ) : (
                    <p style={isAr ? { textAlign: 'justify', textAlignLast: 'right' } : undefined}>
                      {para}
                    </p>
                  )}

                  {/* Insert an inline image after paragraph 1 if gallery has 1+ images */}
                  {i === 0 && gallery[0] && (
                    <figure className="not-prose my-10 -mx-2 md:-mx-4">
                      <button
                        type="button"
                        onClick={() => openLightbox(0)}
                        data-testid="inline-gallery-image"
                        aria-label={isAr ? 'فتح الصورة' : 'Open image'}
                        className="group block w-full overflow-hidden rounded-3xl shadow-xl
                                   focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
                      >
                        <img
                          src={gallery[0]}
                          alt=""
                          className="w-full h-[280px] md:h-[420px] object-cover transition-transform duration-700 group-hover:scale-105 cursor-zoom-in"
                        />
                      </button>
                    </figure>
                  )}

                  {/* Pull quote after paragraph 2 */}
                  {i === 1 && pullQuote && (
                    <blockquote
                      className={`not-prose my-10 px-6 md:px-8 py-6 rounded-2xl border-s-4 ${theme.quoteBorder} ${theme.quoteBg}`}
                      data-testid="article-pull-quote"
                    >
                      <p className={`text-xl md:text-2xl leading-relaxed font-semibold ${theme.quoteText}`}>
                        &ldquo;{pullQuote}&rdquo;
                      </p>
                    </blockquote>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Gallery */}
            {gallery.length > 1 && (
              <section className="mt-16" data-testid="article-gallery">
                <h3 className={`text-xl md:text-2xl font-bold mb-6 text-slate-900 dark:text-white ${isAr ? 'text-right' : 'text-left'}`}>
                  {isAr ? 'معرض الصور' : 'Image Gallery'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  {gallery.slice(1).map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => openLightbox(idx + 1)}
                      data-testid={`gallery-image-${idx}`}
                      aria-label={isAr ? 'فتح الصورة' : 'Open image'}
                      className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all
                                aspect-[4/3] block focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 cursor-zoom-in"
                    >
                      <img
                        src={img}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className={`absolute inset-0 ${theme.accentBg} opacity-0 group-hover:opacity-30 transition-opacity duration-500 mix-blend-multiply`} />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Bottom back link */}
            <div className={`mt-16 pt-8 border-t border-slate-200 dark:border-slate-800 ${isAr ? 'text-right' : 'text-left'}`}>
              <Link
                to="/news"
                data-testid="article-back-bottom"
                className={`group inline-flex items-center gap-2 text-sm font-semibold ${theme.accentText} hover:gap-3 transition-all ${isAr ? 'flex-row-reverse' : ''}`}
              >
                <ArrowLead className="w-4 h-4" />
                <span>{isAr ? 'العودة إلى جميع الأخبار' : 'Back to all news'}</span>
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* ============ RELATED ============ */}
      {related.length > 0 && (
        <section className="relative py-16 bg-white dark:bg-slate-950" data-testid="related-articles">
          <div className="container mx-auto px-4">
            <div className={`max-w-3xl mx-auto mb-10 ${isAr ? 'text-right' : 'text-left'}`}>
              <div className={`flex items-center gap-3 mb-2`}>
                <span className={`text-xs font-mono tracking-[0.3em] ${theme.accentText} mt-1`}>/ RELATED</span>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                  {isAr ? 'مقالات قد تهمّك' : 'You may also like'}
                </h2>
              </div>
              <div className={`mt-2 h-px w-20 bg-gradient-to-r ${theme.accentBg.replace('bg-gradient-to-br','from')} opacity-60`} />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {related.map((r, i) => {
                const rt = THEMES[r.theme] || THEMES.sea;
                return (
                  <Link
                    key={r.id}
                    to={`/news/${r.id}`}
                    data-testid={`related-card-${r.id}`}
                    className="group block rounded-2xl overflow-hidden bg-white dark:bg-slate-900
                               border border-slate-200 dark:border-slate-800
                               hover:border-slate-300 dark:hover:border-slate-700
                               hover:shadow-xl hover:-translate-y-1 transition-all duration-500 animate-fade-in-up"
                    style={{ animationDelay: `${i * 0.06}s` }}
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={r.image}
                        alt={isAr ? r.title_ar : r.title_en}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                      <div className={`absolute top-3 ${isAr ? 'left-3' : 'right-3'}`}>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur-md ${rt.chipClass}`}>
                          <rt.Icon className="w-3 h-3" />
                          {isAr ? r.tag_ar : r.tag_en}
                        </span>
                      </div>
                    </div>
                    <div className={`p-5 ${isAr ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                        <Calendar className="w-3 h-3" />
                        <span>{isAr ? r.date_ar : r.date_en}</span>
                      </div>
                      <h3 className="text-base font-bold leading-snug text-slate-900 dark:text-white">
                        {isAr ? r.title_ar : r.title_en}
                      </h3>
                      <div className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${rt.accentText} group-hover:gap-2.5 transition-all ${isAr ? 'flex-row-reverse' : ''}`}>
                        {isAr ? 'اقرأ المزيد' : 'Read More'}
                        <ArrowTrail className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ LIGHTBOX ============ */}
      {isLightboxOpen && gallery[lightboxIndex] && (
        <div
          data-testid="article-lightbox"
          role="dialog"
          aria-modal="true"
          dir={isAr ? 'rtl' : 'ltr'}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in-up"
          onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            data-testid="lightbox-close"
            aria-label={isAr ? 'إغلاق' : 'Close'}
            className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full flex items-center justify-center
                       bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div
            data-testid="lightbox-counter"
            dir="ltr"
            className="absolute top-6 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full
                       bg-white/10 backdrop-blur text-white/90 text-xs font-mono tracking-wider"
          >
            {lightboxIndex + 1} / {gallery.length}
          </div>

          {/* Previous (visually left arrow) */}
          {gallery.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); (isAr ? showNext : showPrev)(); }}
              data-testid="lightbox-prev"
              aria-label={isAr ? 'الصورة التالية' : 'Previous image'}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10
                         w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center
                         bg-white/10 hover:bg-white/25 text-white transition-all hover:scale-110 backdrop-blur"
            >
              <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
            </button>
          )}

          {/* Image */}
          <div
            className="relative max-w-[92vw] max-h-[85vh] flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={lightboxIndex}
              src={gallery[lightboxIndex]}
              alt=""
              data-testid="lightbox-image"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl animate-fade-in-up select-none"
              draggable={false}
            />
          </div>

          {/* Next (visually right arrow) */}
          {gallery.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); (isAr ? showPrev : showNext)(); }}
              data-testid="lightbox-next"
              aria-label={isAr ? 'الصورة السابقة' : 'Next image'}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10
                         w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center
                         bg-white/10 hover:bg-white/25 text-white transition-all hover:scale-110 backdrop-blur"
            >
              <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
            </button>
          )}

          {/* Thumbnail strip */}
          {gallery.length > 1 && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-2 rounded-2xl
                         bg-white/10 backdrop-blur max-w-[92vw] overflow-x-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIndex(idx)}
                  data-testid={`lightbox-thumb-${idx}`}
                  aria-label={`${isAr ? 'صورة' : 'Image'} ${idx + 1}`}
                  className={`relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden
                              transition-all duration-200
                              ${idx === lightboxIndex
                                ? 'ring-2 ring-white scale-105'
                                : 'opacity-60 hover:opacity-100'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== COPY-LINK TOAST ===== */}
      <div
        data-testid="copy-link-toast"
        role="status"
        aria-live="polite"
        dir={isAr ? 'rtl' : 'ltr'}
        className={`fixed bottom-8 left-1/2 z-[200] pointer-events-none
                    transition-all duration-300 ease-out
                    ${toastVisible
                      ? 'opacity-100 -translate-x-1/2 translate-y-0'
                      : 'opacity-0 -translate-x-1/2 translate-y-4'}`}
      >
        <div
          className={`inline-flex items-center gap-2.5 px-5 py-3 rounded-full
                      bg-white/95 dark:bg-slate-900/95
                      border border-slate-200/80 dark:border-slate-700/80
                      shadow-[0_10px_40px_-10px_rgba(0,0,0,0.25)]
                      dark:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)]
                      backdrop-blur-xl
                      text-sm font-semibold text-slate-800 dark:text-slate-100
                      ${isAr ? 'flex-row-reverse' : ''}`}
        >
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center
                       bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
          <span>{isAr ? 'تم نسخ الرابط' : 'Link copied'}</span>
        </div>
      </div>
    </div>
  );
};

const ShareBtn = ({ testId, label, href, Icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    data-testid={testId}
    className="w-9 h-9 rounded-full flex items-center justify-center
               bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
               text-slate-600 dark:text-slate-300
               hover:text-white hover:bg-slate-900 dark:hover:bg-white dark:hover:text-slate-900
               transition-all hover:-translate-y-0.5"
  >
    <Icon className="w-4 h-4" />
  </a>
);

export default NewsArticlePage;
